import { useRef } from 'react';

import { Card, Empty, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import {
  TiendaPerfectaReportRoute,
  TiendaPerfectaRouteReportContext,
  TiendaPerfectaRouteStop,
} from 'models/TiendaPerfecta';

import { useFlipReorder } from '../hooks/useFlipReorder';
import {
  StopUiState,
  TIMELINE_COLORS,
  TIMELINE_COMPACT_MAX_WIDTH,
  getStopUiState,
} from '../types';
import {
  formatTime,
  getAxisTicks,
  getTrackPosition,
  getUserWindow,
  isReportToday,
  toInstant,
} from '../utils/time';
import AnimatedNumber from './AnimatedNumber';

const I18N_BASE = 'scenes.route_report.components.timeline';

const COLOR_BY_STATE: Record<StopUiState, string> = {
  executed: TIMELINE_COLORS.executed,
  not_executed: TIMELINE_COLORS.notExecuted,
  on_going: TIMELINE_COLORS.onGoing,
  pending: TIMELINE_COLORS.pending,
};

/** El instante que representa a la parada depende de su estado. */
const stopInstant = (stop: TiendaPerfectaRouteStop): string | null =>
  stop.status === 'finished' ? stop.finishedAt : stop.startedAt;

interface StopTooltipProps {
  stop: TiendaPerfectaRouteStop;
  state: StopUiState;
  time: string | null;
  children: React.ReactElement;
}

function StopTooltip({ stop, state, time, children }: StopTooltipProps) {
  const { t } = useTranslation();

  return (
    <Tooltip
      title={
        <div className="route-timeline-tip">
          <b>{stop.label || stop.code}</b>
          <span className="route-timeline-tip__code">{stop.code}</span>
          <span className="route-timeline-tip__row">
            <span className="route-timeline-tip__time">{time ?? '—'}</span>
            <span
              className="route-timeline-tip__state"
              style={{ color: COLOR_BY_STATE[state] }}
            >
              <i />
              {t(`scenes.route_report.stop_states.${state}`)}
            </span>
          </span>
          {stop.bypass && (
            <span className="route-timeline-tip__bypass">
              {stop.bypass.reason}{' '}
              <span className="route-timeline-tip__code">
                {stop.bypass.code}
              </span>
              {/* El comentario de terreno solo cabe acá: en la grilla la columna
                  es angosta y lo truncaría hasta volverlo inútil. */}
              {stop.bypass.note && (
                <span className="route-timeline-tip__note">
                  {stop.bypass.note}
                </span>
              )}
            </span>
          )}
        </div>
      }
    >
      {children}
    </Tooltip>
  );
}

interface RouteTimelineProps {
  routes: TiendaPerfectaReportRoute[];
  context: TiendaPerfectaRouteReportContext;
  userTimeZone: string;
}

/**
 * Las unidades que ya arrancaron, ordenadas por menor avance.
 *
 * No es un segundo arreglo del servidor: se deriva de las mismas `routes` que ya
 * llegaron, filtrando `not_started` y ordenando por paradas cerradas.
 *
 * Debajo de `TIMELINE_COMPACT_MAX_WIDTH` el eje horario deja de ser usable —las
 * marcas quedan más juntas que el objetivo táctil mínimo— y se reemplaza por una
 * barra segmentada, una parada por segmento en su orden real. Lo que se pierde
 * ahí, la hora exacta de cada marca, sigue disponible en la grilla de abajo.
 */
function RouteTimeline({ routes, context, userTimeZone }: RouteTimelineProps) {
  const { t } = useTranslation();
  const compact = useMediaQuery({
    query: `(max-width: ${TIMELINE_COMPACT_MAX_WIDTH}px)`,
  });

  // Al cerrarse una parada la unidad cambia de puesto: se la acompaña con el
  // desplazamiento en vez de dejar que reaparezca en otro lugar.
  const listRef = useRef<HTMLDivElement>(null);
  useFlipReorder(listRef);

  const rows = routes
    .filter((route) => route.status !== 'not_started')
    .sort((a, b) => a.finishedStopCount - b.finishedStopCount);

  const window = getUserWindow(
    context.date,
    context.windowStart,
    context.windowEnd,
    context.timezone
  );
  const ticks = getAxisTicks(window, userTimeZone);

  // La línea "ahora" sale de `context.serverTime`, la misma hora que ya se usa
  // para el «corte» del avance del día: cero campos nuevos y una sola fuente
  // de verdad. Solo se dibuja si el reporte es del día en curso —sobre un día
  // cerrado hace horas quedaría clavada en un extremo sin informar nada— y
  // nunca en compacto, donde el eje horizontal deja de ser la hora.
  const showNow =
    !compact &&
    isReportToday(context.date, context.serverTime, context.timezone);
  const nowPosition = getTrackPosition(toInstant(context.serverTime), window);
  const nowTime = formatTime(context.serverTime, userTimeZone);
  // Igual que las etiquetas del eje: pegada a un borde deja de centrarse.
  const nowPillEdge =
    nowPosition < 1 ? 'start' : nowPosition > 99 ? 'end' : null;

  const legend: StopUiState[] = compact
    ? ['executed', 'not_executed', 'on_going', 'pending']
    : ['executed', 'not_executed'];

  return (
    <Card
      className="route-timeline"
      title={
        <div className="route-timeline__head">
          <div>
            <span className="route-timeline__title">
              {t(`${I18N_BASE}.title`)}
            </span>
            <span className="route-timeline__subtitle">
              {compact
                ? t(`${I18N_BASE}.subtitle_compact`)
                : t(`${I18N_BASE}.subtitle`)}
            </span>
          </div>
          <div className="route-timeline__legend">
            {legend.map((state) => (
              <span key={state}>
                <i style={{ background: COLOR_BY_STATE[state] }} />
                {t(`scenes.route_report.stop_states.${state}`)}
              </span>
            ))}
          </div>
        </div>
      }
    >
      <div
        className={`route-timeline__body${
          !compact ? ' route-timeline__body--now' : ''
        }`}
      >
        {showNow && (
          // Overlay hermano del eje y de la lista, nunca dentro de una fila
          // —si viviera ahí habría que dibujar una línea por ruta y alinearlas
          // a mano—. Repite la misma grilla para que su columna central caiga
          // exacto sobre los tracks. `aria-hidden`: es una ayuda visual, la
          // hora exacta ya está en el texto del avance del día.
          <div className="route-timeline__now" aria-hidden>
            <div className="route-timeline__grid">
              <div />
              <div className="route-timeline__now-col">
                <span
                  className="route-timeline__now-line"
                  style={{ left: `${nowPosition}%` }}
                />
                <span
                  className={`route-timeline__now-pill${
                    nowPillEdge
                      ? ` route-timeline__now-pill--${nowPillEdge}`
                      : ''
                  }`}
                  style={nowPillEdge ? undefined : { left: `${nowPosition}%` }}
                >
                  {t(`${I18N_BASE}.now`)} <span>{nowTime}</span>
                </span>
              </div>
              <div />
            </div>
          </div>
        )}

        {!compact && (
          <div className="route-timeline__axis-wrap">
            <div className="route-timeline__grid">
              <div />
              <div className="route-timeline__axis">
                {ticks.map((tick) => (
                  <span
                    key={tick.instantMs}
                    className="route-timeline__axis-label"
                    style={
                      tick.position < 1
                        ? { left: 0, transform: 'none' }
                        : tick.position > 99
                        ? { right: 0, left: 'auto', transform: 'none' }
                        : { left: `${tick.position}%` }
                    }
                  >
                    {tick.label}
                  </span>
                ))}
              </div>
              <div />
            </div>
          </div>
        )}

        <div className="route-timeline__scroll" ref={listRef}>
          {rows.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t(`${I18N_BASE}.empty`)}
            />
          ) : (
            rows.map((route) => {
              const closed = route.stops
                .map((stop) => ({ stop, iso: stopInstant(stop) }))
                .filter(
                  (entry) =>
                    entry.stop.status === 'finished' && entry.iso !== null
                )
                .map((entry) => ({
                  stop: entry.stop,
                  iso: entry.iso as string,
                  instantMs: toInstant(entry.iso as string),
                }));

              const first = closed.length
                ? getTrackPosition(closed[0].instantMs, window)
                : 0;
              const last = closed.length
                ? getTrackPosition(closed[closed.length - 1].instantMs, window)
                : 0;

              const percentage = route.stopCount
                ? Math.round((route.finishedStopCount / route.stopCount) * 100)
                : 0;

              return (
                <div
                  className="route-timeline__row"
                  key={route.routeId}
                  data-flip-key={route.routeId}
                >
                  <div className="route-timeline__grid">
                    <div className="route-timeline__id">
                      <div
                        className="route-timeline__van"
                        title={
                          route.van
                            ? t('scenes.route_report.van_plate', {
                                plate: route.van.plate,
                              })
                            : undefined
                        }
                      >
                        {route.van
                          ? route.van.name || route.van.plate
                          : t(`${I18N_BASE}.no_van`)}
                      </div>
                      <div className="route-timeline__meta">
                        {route.code}
                        {route.distributor
                          ? ` · ${route.distributor.tradeName}`
                          : ''}
                      </div>
                    </div>

                    {!compact && (
                      <div className="route-timeline__track">
                        <span
                          className="route-timeline__span"
                          style={{
                            left: `${first}%`,
                            width: `${Math.max(last - first, 0)}%`,
                          }}
                        />
                        {closed.map(({ stop, iso, instantMs }) => {
                          const state = getStopUiState(
                            stop.status,
                            Boolean(stop.bypass)
                          );

                          return (
                            <StopTooltip
                              key={`${route.routeId}-${stop.sequence}`}
                              stop={stop}
                              state={state}
                              time={formatTime(iso, userTimeZone)}
                            >
                              <i
                                className={`route-timeline__tick route-timeline__tick--${state}`}
                                style={{
                                  left: `${getTrackPosition(
                                    instantMs,
                                    window
                                  )}%`,
                                }}
                              />
                            </StopTooltip>
                          );
                        })}
                      </div>
                    )}

                    <div className="route-timeline__count">
                      <AnimatedNumber
                        className="route-timeline__count-done"
                        value={route.finishedStopCount}
                      />{' '}
                      / {route.stopCount}
                    </div>

                    {compact && (
                      <div className="route-timeline__mobile">
                        <div className="route-timeline__segments">
                          {route.stops.map((stop) => {
                            const state = getStopUiState(
                              stop.status,
                              Boolean(stop.bypass)
                            );
                            const iso = stopInstant(stop);

                            return (
                              <StopTooltip
                                key={`${route.routeId}-seg-${stop.sequence}`}
                                stop={stop}
                                state={state}
                                time={
                                  iso ? formatTime(iso, userTimeZone) : null
                                }
                              >
                                <i
                                  className={`route-timeline__segment route-timeline__segment--${state}`}
                                />
                              </StopTooltip>
                            );
                          })}
                        </div>
                        <div className="route-timeline__mobile-foot">
                          <span>
                            {closed.length
                              ? `${formatTime(
                                  closed[0].iso,
                                  userTimeZone
                                )} – ${formatTime(
                                  closed[closed.length - 1].iso,
                                  userTimeZone
                                )}`
                              : '—'}
                          </span>
                          <b>{percentage}%</b>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Card>
  );
}

export default RouteTimeline;
